import { useState } from 'react';
import { useMenusDesligadosDaEmpresa, useSalvarMenus } from '@/api/menus';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { achatarItens, GRUPOS_NAVEGACAO } from '@/components/app-shell/navegacao';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/**
 * Configuracoes > Empresa > Menus: liga/desliga os itens do menu da empresa, por grupo ou por item. So exibicao: desligado some do
 * menu, da busca (Ctrl K), dos favoritos e do Copiloto, e a tela responde "area desativada para esta empresa". Nada e apagado.
 * Vale por cima do perfil de atividade. So ADMIN_TENANT e SUPERADMIN (empresas.gerenciar) alteram; a API repete a regra.
 */
export function MenusEmpresa({ editavel }: { editavel: boolean }) {
  const { empresaId } = useSessao();
  const atuais = useMenusDesligadosDaEmpresa();
  return (
    <section aria-label="Menus da empresa" data-testid="secao-menus-empresa" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-title font-semibold">Menus</h2>
        <p className="mt-1 text-small text-ink-secondary">
          Escolha o que aparece no menu desta empresa. O que ficar desligado some do menu, da busca, dos favoritos e do Copiloto; nada é apagado e dá para religar a qualquer hora.
        </p>
      </div>
      {atuais ? (
        <Escolha key={`${empresaId}:${atuais.join(',')}`} atuais={atuais} editavel={editavel} />
      ) : (
        <p role="status" className="text-small text-ink-muted">
          Carregando menus…
        </p>
      )}
    </section>
  );
}

function Escolha({ atuais, editavel }: { atuais: readonly string[]; editavel: boolean }) {
  const [desligados, setDesligados] = useState<string[]>([...atuais]);
  const salvar = useSalvarMenus();
  const erro = salvar.isError ? mensagemErro(salvar.error) : null;
  const alterado = desligados.length !== atuais.length || desligados.some((r) => !atuais.includes(r));

  function definir(rotas: string[], ligado: boolean) {
    setDesligados((atual) => (ligado ? atual.filter((r) => !rotas.includes(r)) : [...new Set([...atual, ...rotas])]));
  }

  return (
    <div className="flex flex-col gap-4">
      {GRUPOS_NAVEGACAO.map((grupo) => {
        const itens = achatarItens(grupo.itens).flatMap((i) => (i.rota ? [{ titulo: i.titulo, rota: i.rota as string }] : []));
        const rotas = itens.map((i) => i.rota);
        const ligados = rotas.filter((r) => !desligados.includes(r)).length;
        const todos = ligados === rotas.length;
        return (
          <fieldset key={grupo.titulo} className="rounded-xl border border-border p-4" data-testid={`menu-grupo-${grupo.titulo}`}>
            <legend className="px-1 text-body font-semibold">{grupo.titulo}</legend>
            <label className="flex items-center gap-2 text-small font-medium">
              <input
                type="checkbox"
                aria-label={`Grupo ${grupo.titulo}`}
                checked={todos}
                ref={(el) => {
                  if (el) el.indeterminate = ligados > 0 && !todos;
                }}
                disabled={!editavel || salvar.isPending}
                onChange={(e) => definir(rotas, e.target.checked)}
              />
              Grupo inteiro ({ligados} de {rotas.length} ligados)
            </label>
            <ul className="mt-2 grid gap-1 sm:grid-cols-2">
              {itens.map((i) => (
                <li key={i.rota}>
                  <label className="flex items-center gap-2 text-small">
                    <input
                      type="checkbox"
                      aria-label={i.titulo}
                      checked={!desligados.includes(i.rota)}
                      disabled={!editavel || salvar.isPending}
                      onChange={(e) => definir([i.rota], e.target.checked)}
                    />
                    {i.titulo}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        );
      })}
      <div>
        <Button type="button" aria-label="Gravar menus" disabled={!editavel || !alterado || salvar.isPending} onClick={() => salvar.mutate({ menusDesligados: desligados as never })}>
          Gravar menus
        </Button>
      </div>
      {erro && (
        <p role="alert" className="text-small text-danger-ink">
          {erro}
        </p>
      )}
    </div>
  );
}
